import React, { useEffect, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, X, Trash2 } from "lucide-react";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";

const ACCENT = "#169FA7";

export default function DeleteModal({
  setDeleteId,
  DeleteId,
  Model,
  setDone,
  setLoding,
  done,
  open,
  setOpen,
  title = "Are you sure?",
  message = "Do you really want to delete this record? This action cannot be undone.",
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  requireConfirm = false,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const closeBtnRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "unset";
    if (open) setTimeout(() => closeBtnRef.current?.focus(), 10);
    return () => (document.body.style.overflow = "unset");
  }, [open]);

  useEffect(() => {
    const onKey = (e) => open && e.key === "Escape" && safeClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const safeClose = useCallback(() => {
    if (submitting) return;
    setDeleteId?.(null);
    setOpen?.(false);
  }, [setDeleteId, setOpen, submitting]);

  const deleteRecord = useCallback(
    async (id) => {
      if (!id || submitting) return;
      if (requireConfirm && confirmText.trim().toUpperCase() !== "DELETE") {
        toast.warn('Please type "DELETE" to confirm.');
        return;
      }
      try {
        setSubmitting(true);
        setLoding?.(true);
        const res = await axiosInstance.delete(`/${Model}/destroy/${id}`);
        const data = res?.data;
        if (data) {
          toast.success(data.message || "Deleted successfully.");
          setDeleteId?.(null);
          setDone?.(!done);
          setOpen?.(false);
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "Deletion failed");
        console.error("Deletion failed:", error?.response?.data || error);
      } finally {
        setSubmitting(false);
        setLoding?.(false);
      }
    },
    [Model, confirmText, done, requireConfirm, setDeleteId, setDone, setLoding, setOpen, submitting]
  );

  const canConfirm = !requireConfirm || confirmText.trim().toUpperCase() === "DELETE";

  return (
    <AnimatePresence>
      {open && DeleteId ? (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Overlay */}
          <div className="absolute inset-0 bg-black/70" onClick={safeClose} aria-hidden="true" />

          {/* Dialog */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-modal-title"
            aria-describedby="delete-modal-desc"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.94 }}
            transition={{ duration: 0.22 }}
            className="relative max-w-md w-[92%] mx-auto rounded-2xl bg-surface shadow-2xl border border-line/70 p-6"
          >
            {/* Close */}
            <button
              ref={closeBtnRef}
              onClick={safeClose}
              className="absolute top-3 right-3 p-2 rounded-lg hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-offset-2"
              style={{ "--tw-ring-color": ACCENT }}
              aria-label="Close dialog"
            >
              <X className="w-5 h-5 text-muted" />
            </button>

            {/* Icon */}
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>

            {/* Title & text */}
            <h2 id="delete-modal-title" className="text-lg font-semibold text-ink text-center">
              {title}
            </h2>
            <p id="delete-modal-desc" className="mt-2 text-sm text-muted text-center">
              {message}
            </p>

            {requireConfirm && (
              <div className="mt-5">
                <label className="block text-sm font-medium text-ink mb-1">
                  Type <span className="font-semibold">DELETE</span> to confirm
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  className="w-full h-11 rounded-md border border-line bg-surface-2 px-3 text-ink focus:outline-none focus:ring-2"
                  style={{ "--tw-ring-color": ACCENT }}
                />
              </div>
            )}

            {/* Actions */}
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={safeClose}
                disabled={submitting}
                className="px-4 h-10 rounded-md border border-line text-ink hover:bg-surface-2"
              >
                {cancelLabel}
              </button>

              <button
                onClick={() => deleteRecord(DeleteId)}
                disabled={submitting || !canConfirm}
                className="inline-flex items-center px-4 h-10 rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-60"
              >
                {submitting ? (
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                {confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
